import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Anchor,
  Box,
  Button,
  Center,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconAlertCircle, IconMail } from '@tabler/icons-react';
import { forgotPasswordRequest } from './auth-api';
import { ApiError } from '../lib/api-error';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await forgotPasswordRequest(email.trim());
      setSent(true);
    } catch (err) {
      // The API path is no-enumeration so genuine 200s don't throw. Anything
      // here is a rate-limit or network failure.
      if (err instanceof ApiError && err.code === 'rate_limited') {
        setError('Too many reset requests from this address. Try again in a few minutes.');
      } else if (err instanceof ApiError && err.code === 'validation_failed') {
        setError('Please enter a valid email address.');
      } else {
        setError('Could not reach the server. Try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Center h="100vh" bg="var(--mantine-color-default-hover)">
      <Paper shadow="md" radius="md" p="xl" w={420} withBorder>
        <Stack gap="md">
          <Box>
            <Title order={2}>Forgot your password?</Title>
            <Text c="dimmed" size="sm">
              Enter the email on your account and we&apos;ll send you a reset link.
            </Text>
          </Box>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              {error}
            </Alert>
          )}

          {sent ? (
            <Alert icon={<IconMail size={16} />} color="pine" variant="light">
              <Text size="sm">
                If an account exists for <strong>{email}</strong>, a reset link is on its way.
                It expires in 15 minutes. Check your spam folder if you don&apos;t see it.
              </Text>
            </Alert>
          ) : (
            <form onSubmit={onSubmit}>
              <Stack gap="sm">
                <TextInput
                  label="Email address"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  required
                  autoFocus
                />
                <Button type="submit" loading={submitting} fullWidth mt="xs">
                  Send reset link
                </Button>
              </Stack>
            </form>
          )}

          <Text size="xs" c="dimmed" ta="center">
            <Anchor component={Link} to="/login">
              Back to sign in
            </Anchor>
          </Text>
        </Stack>
      </Paper>
    </Center>
  );
}
